// FiboPromptTree/TreeNode.tsx
import { Box } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { PreservedIcon } from "../../../../assets/images/icons/PreservedIcon";
import BriaTooltip from "../../../../components/common/BriaTooltip/BriaTooltip";
import { useAppStore } from "../../../../hooks/useStores";
import { RGBColor } from "../../../../layout/LabsLayout/Results/Canvas/components/FiboColorPicker";
import type { JSONValue } from "../../../../models/fibo";
import { isFibo2App } from "../../../../utils";
import { hasChangeForAny, hasChangeUnder, isArr, isObj } from "../../../../utils/fiboLabs";
import { isVglNumber } from "../../../../utils/vglTokens";
import { Badge } from "./Badge";
import { BBoxPoint } from "./BoundingBoxInput";
import { BoundingBoxTreeNode } from "./BoundingBoxTreeNode";
import { ColorsTreeNode } from "./ColorsTreeNode";
import styles from "./FiboPromptTree.module.scss";
import { KeypointTreeNode, KeypointValue } from "./KeypointTreeNode";

// VGL text leaves that are editable in the FIBO 2 tree (refine mode). Extend as needed.
// "text" covers a text line's own string content, under lines[].
const EDITABLE_VGL_KEYS = ["name", "description", "text"];

// Top-level entries the canvas can select: the flat "objects" array (BBQ/FIBO 1.1), or FIBO 2's
// three separate layer arrays.
const TOP_LEVEL_KEYS = new Set(["objects", "foreground", "midground", "background"]);

/** Does this JSON value (or anything nested inside it) itself own a bounding_boxes field? */
const containsBoundingBoxesDeep = (value: JSONValue): boolean => {
	if (isObj(value)) {
		const obj = value as Record<string, JSONValue>;
		if (isArr(obj.bounding_boxes) && (obj.bounding_boxes as JSONValue[]).length > 0) return true;
		return Object.values(obj).some(containsBoundingBoxesDeep);
	}
	if (isArr(value)) return (value as JSONValue[]).some(containsBoundingBoxesDeep);
	return false;
};

/** Inline click-to-edit text input for an editable string leaf. Commits on blur/Enter. */
const EditableLeaf = observer(
	({
		value,
		path,
		className,
		onValueChange,
	}: {
		value: string;
		path: string[];
		className?: string;
		onValueChange?: (path: string[], value: JSONValue) => void;
	}) => {
		const [draft, setDraft] = useState(value);
		useEffect(() => setDraft(value), [value]);

		const commit = () => {
			if (draft !== value) onValueChange?.(path, draft);
		};

		return (
			<input
				className={clsx(className, styles.valueInput)}
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
				onBlur={commit}
				onKeyDown={(e) => {
					if (e.key === "Enter") (e.target as HTMLInputElement).blur();
					if (e.key === "Escape") setDraft(value);
					e.stopPropagation();
				}}
				onClick={(e) => e.stopPropagation()}
				onMouseDown={(e) => e.stopPropagation()}
			/>
		);
	},
);

// Channel/coordinate values may be model tokens ("<color_222>", "<coord_33.2>") rather
// than numbers — `isVglNumber` accepts both, so tokenized vgls still get the editors.
const isRGBColor = (v: unknown): v is RGBColor => {
	if (!isObj(v)) return false;
	const obj = v as Record<string, unknown>;
	const hasShortFormat = isVglNumber(obj.r) && isVglNumber(obj.g) && isVglNumber(obj.b);
	const hasLongFormat = isVglNumber(obj.red) && isVglNumber(obj.green) && isVglNumber(obj.blue);
	return hasShortFormat || hasLongFormat;
};

const isBBoxPoint = (v: unknown): v is BBoxPoint => {
	if (!isObj(v)) return false;
	const obj = v as Record<string, unknown>;
	return isVglNumber(obj.x) && isVglNumber(obj.y);
};

const isBoundingBoxItemCheck = (v: unknown): boolean => {
	if (!isObj(v)) return false;
	const obj = v as Record<string, JSONValue>;
	return (
		("top_left" in obj && isBBoxPoint((obj as Record<string, unknown>).top_left)) ||
		("bottom_right" in obj && isBBoxPoint((obj as Record<string, unknown>).bottom_right))
	);
};

const isKeypointItem = (v: unknown): v is KeypointValue => {
	if (!isObj(v)) return false;
	const obj = v as Record<string, unknown>;
	if (typeof obj.name !== "string" || !isObj(obj.point)) return false;
	const point = obj.point as Record<string, unknown>;
	return isVglNumber(point.x) && isVglNumber(point.y);
};

export type NodeProps = {
	k?: string;
	v: JSONValue;
	path: string[];
	changed: Set<string>;
	showPreserved: boolean;
	subtreeBases?: string[];
	renderPreservedIcon?: React.ReactNode;
	isEditable?: boolean;
	onValueChange?: (path: string[], value: JSONValue) => void;
};

const TreeNode = observer(
	({
		k,
		v,
		path,
		changed,
		showPreserved,
		subtreeBases,
		renderPreservedIcon,
		isEditable,
		onValueChange,
	}: NodeProps) => {
		const { fiboStore } = useAppStore();
		const { structuredPromptStore, activeTab } = fiboStore;
		const isRefineMode = activeTab === "refine";
		const selectedGroup = fiboStore.getSelectedFiboGroup();
		const selectedImageId = (selectedGroup?.selectedImage as { id?: string })?.id;
		const key = path.join(".");
		const isGroup = isObj(v) || isArr(v);
		const open = structuredPromptStore.isNodeOpen(key);
		const setOpen = React.useCallback(
			(openOrUpdater: boolean | ((prev: boolean) => boolean)) => {
				const currentOpen = structuredPromptStore.isNodeOpen(key);
				const newValue = typeof openOrUpdater === "function" ? openOrUpdater(currentOpen) : openOrUpdater;
				structuredPromptStore.setNodeOpen(key, newValue);
			},
			[key, structuredPromptStore],
		);
		const [isTruncated, setIsTruncated] = React.useState(false);
		const valueRef = React.useRef<HTMLSpanElement>(null);
		const nodeRef = React.useRef<HTMLDivElement>(null);
		const { hoveredObjectId, selectedObjectId, setHoveredObjectId, setSelectedObjectId } = structuredPromptStore;

		const isChangedLeaf = changed.has(key);
		const subtreeChanged = subtreeBases
			? hasChangeForAny(changed, subtreeBases)
			: isGroup
				? hasChangeUnder(changed, key)
				: isChangedLeaf;

		const isPreserved = showPreserved && (isGroup ? !subtreeChanged : !isChangedLeaf);

		const colorKey = k?.toLowerCase();
		const colorGroups = new Set(["colors", "color_palette", "skin_color"]);
		const isColorsArray = colorKey && colorGroups.has(colorKey) && isArr(v) && (v as JSONValue[]).every(isRGBColor);

		const isBoundingBoxItem =
			isObj(v) &&
			(("top_left" in (v as Record<string, JSONValue>) && isBBoxPoint((v as Record<string, unknown>).top_left)) ||
				("bottom_right" in (v as Record<string, JSONValue>) &&
					isBBoxPoint((v as Record<string, unknown>).bottom_right)));

		const isInBoundingBoxesArray = path.length >= 2 && path[path.length - 2] === "bounding_boxes";

		const isBoundingBoxesArray =
			k?.toLowerCase() === "bounding_boxes" && isArr(v) && (v as JSONValue[]).every(isBoundingBoxItemCheck);

		const isKeypointsArray = k === "keypoints" && isArr(v) && (v as JSONValue[]).every(isKeypointItem);
		const isInKeypointsArray = path.length >= 2 && path[path.length - 2] === "keypoints";

		// Top-level canvas-selectable entry — matched by JSON path key rather than a canvas-only id,
		// since FIBO 2 splits objects across three separate layer arrays sharing one global counter
		// (see StructuredPromptImageCanvas.tsx's pathKeyOf), so a bare "object-N" id alone can't be
		// mapped back to a path here.
		const isObjectItem = path.length === 2 && TOP_LEVEL_KEYS.has(path[0]);
		// Only apply hover/selection logic in refine mode
		const isCanvasHovered = isRefineMode && isObjectItem && hoveredObjectId === key;
		const isCanvasSelected = isRefineMode && isObjectItem && selectedObjectId === key;

		useEffect(() => {
			if (!isGroup && valueRef.current) {
				const timeoutId = setTimeout(() => {
					const element = valueRef.current;
					if (element) {
						const isTruncatedNow = element.scrollWidth > element.clientWidth;
						setIsTruncated(isTruncatedNow);
					}
				}, 0);

				return () => clearTimeout(timeoutId);
			}
		}, [v, isGroup]);

		useEffect(() => {
			if (!isGroup && valueRef.current) {
				const handleResize = () => {
					const element = valueRef.current;
					if (element) {
						const isTruncatedNow = element.scrollWidth > element.clientWidth;
						setIsTruncated(isTruncatedNow);
					}
				};

				window.addEventListener("resize", handleResize);
				return () => window.removeEventListener("resize", handleResize);
			}
		}, [isGroup]);

		useEffect(() => {
			if (isCanvasSelected) {
				if (isGroup) setOpen(true);
				const timeoutId = setTimeout(
					() => {
						nodeRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
					},
					isGroup ? 250 : 0,
				);
				return () => clearTimeout(timeoutId);
			}
		}, [isCanvasSelected, isGroup, selectedImageId, setOpen]);

		// Open every ancestor of the selected object so it's actually reachable/visible in the tree.
		useEffect(() => {
			if (!isGroup || !selectedObjectId) return;
			const isAncestor = selectedObjectId === key || selectedObjectId.startsWith(`${key}.`);
			if (isAncestor) {
				setOpen(true);
			}
		}, [selectedObjectId, key, isGroup, setOpen]);

		// Also open every nested object WITHIN the selected object's own subtree that owns a
		// bounding box (person parts, clothing/accessories, text lines, ...) — and the array
		// containers leading to them — so nothing bbox-relevant stays hidden once selected.
		useEffect(() => {
			if (!isGroup || !selectedObjectId) return;
			const isWithinSelectedSubtree = key === selectedObjectId || key.startsWith(`${selectedObjectId}.`);
			if (isWithinSelectedSubtree && containsBoundingBoxesDeep(v)) {
				setOpen(true);
			}
		}, [selectedObjectId, key, isGroup, v, setOpen]);

		const badge =
			k === "General" && isObj(v) ? (
				<span className={styles.badge}>{Object.keys(v).length}</span>
			) : (
				<Badge value={v} />
			);

		if (isColorsArray && k) {
			const isColorPalette = k.toLowerCase() === "color_palette";
			return (
				<ColorsTreeNode
					k={k}
					v={v}
					path={path}
					subtreeChanged={subtreeChanged}
					isPreserved={isPreserved}
					collapsible={isColorPalette}
					isEditable={isEditable}
					onValueChange={onValueChange}
				/>
			);
		}

		if (isBoundingBoxesArray && k) {
			const boxes = v as JSONValue[];
			return (
				<Box
					className={clsx(styles.row, {
						[styles.isChanged]: subtreeChanged && !isPreserved,
						[styles.isPreserved]: isPreserved,
					})}
					onClick={(e) => e.stopPropagation()}
				>
					<Box className={styles.header}>
						<span className={clsx(styles.key, styles.mainTitle)}>{k.replace(/_/g, " ")}</span>
						<span className={styles.value}>
							<span>{`[{`}</span>
						</span>
						{isPreserved && (
							<BriaTooltip title="Preserved during refinement">
								<span className={styles.preservedIconWrap}>
									{renderPreservedIcon ?? <PreservedIcon className={styles.preservedIcon} />}
								</span>
							</BriaTooltip>
						)}
					</Box>
					<Box className={styles.boundingBoxContainer}>
						{boxes.map((box, i) => (
							<BoundingBoxTreeNode
								key={i}
								v={box}
								path={[...path, String(i)]}
								subtreeChanged={subtreeChanged}
								isPreserved={isPreserved}
								isEditable={isEditable}
								onValueChange={onValueChange}
							/>
						))}
					</Box>
					<Box className={styles.header}>
						<span className={styles.value}>
							<span>{`}]`}</span>
						</span>
					</Box>
				</Box>
			);
		}

		if (isBoundingBoxItem && isInBoundingBoxesArray) {
			return (
				<BoundingBoxTreeNode
					v={v}
					path={path}
					subtreeChanged={subtreeChanged}
					isPreserved={isPreserved}
					isEditable={isEditable}
					onValueChange={onValueChange}
				/>
			);
		}

		if (isKeypointsArray && k) {
			const keypoints = v as JSONValue[];
			return (
				<Box
					className={clsx(styles.row, {
						[styles.isChanged]: subtreeChanged && !isPreserved,
						[styles.isPreserved]: isPreserved,
					})}
					onClick={(e) => e.stopPropagation()}
				>
					<Box className={styles.header}>
						<span className={clsx(styles.key, styles.mainTitle)}>{k.replace(/_/g, " ")}</span>
						<span className={styles.value}>
							<span>{`[{`}</span>
						</span>
						{isPreserved && (
							<BriaTooltip title="Preserved during refinement">
								<span className={styles.preservedIconWrap}>
									{renderPreservedIcon ?? <PreservedIcon className={styles.preservedIcon} />}
								</span>
							</BriaTooltip>
						)}
					</Box>
					<Box className={styles.boundingBoxContainer}>
						{keypoints.map((kp, i) => (
							<KeypointTreeNode
								key={i}
								v={kp}
								path={[...path, String(i)]}
								subtreeChanged={subtreeChanged}
								isPreserved={isPreserved}
								isEditable={isEditable}
								onValueChange={onValueChange}
							/>
						))}
					</Box>
					<Box className={styles.header}>
						<span className={styles.value}>
							<span>{`}]`}</span>
						</span>
					</Box>
				</Box>
			);
		}

		if (isKeypointItem(v) && isInKeypointsArray) {
			return (
				<KeypointTreeNode
					v={v}
					path={path}
					subtreeChanged={subtreeChanged}
					isPreserved={isPreserved}
					isEditable={isEditable}
					onValueChange={onValueChange}
				/>
			);
		}

		return (
			<Box
				ref={isObjectItem ? nodeRef : undefined}
				className={clsx(styles.row, isGroup ? styles.group : styles.primitive, {
					[styles.isChanged]: subtreeChanged && !isPreserved,
					[styles.isPreserved]: isPreserved,
					[styles.objectItem]: isObjectItem,
					[styles.objectItemHovered]: isCanvasHovered,
					[styles.objectItemSelected]: isCanvasSelected,
				})}
				onMouseEnter={isRefineMode && isObjectItem ? () => setHoveredObjectId(key) : undefined}
				onMouseLeave={isRefineMode && isObjectItem ? () => setHoveredObjectId(null) : undefined}
			>
				<Box
					className={styles.header}
					role={isGroup ? "button" : undefined}
					tabIndex={isGroup ? 0 : -1}
					aria-expanded={isGroup ? open : undefined}
					aria-label={isGroup ? (k ? `${k} group` : "group") : undefined}
					onClick={() => {
						if (isGroup) {
							setOpen((o) => {
								if (isRefineMode && o && isCanvasSelected) {
									setSelectedObjectId(null);
								}
								return !o;
							});
						}
						if (isRefineMode && isObjectItem && !open) setSelectedObjectId(key);
					}}
				>
					{isGroup && <span className={clsx(styles.chevron, open && styles.open)} aria-hidden />}

					{k && (
						<span className={clsx(styles.key, { [styles.mainTitle]: isGroup })}>
							{k.replace(/_/g, " ")}
							{!isGroup && " :"}
						</span>
					)}

					{!isGroup ? (
						isEditable && isFibo2App() && !!k && EDITABLE_VGL_KEYS.includes(k) && typeof v === "string" ? (
							<EditableLeaf
								value={v}
								path={path}
								className={styles.value}
								onValueChange={onValueChange}
							/>
						) : (
							<span className={styles.value}>
								{isTruncated ? (
									<BriaTooltip title={String(v)}>
										<span ref={valueRef}>{String(v)}</span>
									</BriaTooltip>
								) : (
									<span ref={valueRef}>{String(v)}</span>
								)}
							</span>
						)
					) : (
						badge
					)}

					{isPreserved && (
						<BriaTooltip title="Preserved during refinement">
							<span className={styles.preservedIconWrap}>
								{renderPreservedIcon ?? <PreservedIcon className={styles.preservedIcon} />}
							</span>
						</BriaTooltip>
					)}
				</Box>

				{isGroup && (
					<Collapse in={open} timeout={220} collapsedSize={0}>
						<Box className={open ? styles.collapseInnerOpen : styles.collapseInner}>
							<Box className={styles.children}>
								{isArr(v)
									? (v as JSONValue[]).map((item, i) => (
											<TreeNode
												key={i}
												v={item}
												path={[...path, String(i)]}
												changed={changed}
												showPreserved={showPreserved}
												renderPreservedIcon={renderPreservedIcon}
												isEditable={isEditable}
												onValueChange={onValueChange}
											/>
										))
									: Object.entries(v as Record<string, JSONValue>).map(([ck, cv]) => {
											const childPath = subtreeBases ? [ck] : [...path, ck];
											return (
												<TreeNode
													key={ck}
													k={ck}
													v={cv}
													path={childPath}
													changed={changed}
													showPreserved={showPreserved}
													renderPreservedIcon={renderPreservedIcon}
													isEditable={isEditable}
													onValueChange={onValueChange}
												/>
											);
										})}
							</Box>
						</Box>
					</Collapse>
				)}
			</Box>
		);
	},
);

export default TreeNode;
